import ShopifyLogo from '@/assets/icons/shopify.svg'
import { useAdminContext } from '@/contexts/AdminContext'
import { useAuth, useOauth } from "@/contexts/AuthContext"
import { useToast } from "@/contexts/ToastContext"
import { useTranslation } from '@client-app/i18n'
import { authService, VerifyAuthResponse } from "@common/api/authService"
import { Icon } from '@common/index'
import { Gift } from '@phosphor-icons/react'
import { supabase } from '@common/api/authService/supabase'
import { trackGoogleAnalyticsEvent } from '@common/utils/googleAnalytics'
import { User } from "@supabase/supabase-js"
import { useEffect, useState } from "react"
import { captureSentryError, logSentryEvent } from '@/lib/sentry'
import {
    clearPendingPasswordSetup,
    markPendingPasswordSetup,
} from '@/utils/pendingPasswordSetup'
import {
    clearPendingPasswordReset,
    hasPendingPasswordReset,
    markPendingPasswordReset,
    readPendingPasswordReset,
    type PendingPasswordResetState,
} from '@/utils/pendingPasswordReset'
import { resolveBustlyWebRedirect } from '@/utils/webRedirect'
import {
    EMAIL_OTP_RESEND_COOLDOWN_SECONDS,
    getEmailOtpPostSignInRedirect,
    getPasswordSetupRedirect,
    sendEmailOtp,
    verifyEmailOtp,
} from './emailOtpAuth'
import { getNewPasswordError, getPasswordUpdateError } from '@/utils/passwordPolicy'
import { getProductScopedAuthRequestConfig } from './authProductRequestConfig'
import {
    buildAuthReturnUrl,
    buildReferralAuthSearch,
    isReferralAuthPreview,
    startReferralSignInRouting,
    type ReferralSignInState,
} from './referralAuthEntry'
import { getCurrentProductCode, isPluginProduct } from '@common/api/productContext'
import {
    clearStoredReferralAttribution,
    readReferralCode,
    readReferralEntryId,
    saveReferralAttribution,
    type ReferralAttribution,
} from '@common/referral/referralAttribution'
import styles from "./auth.module.scss"
import ProductBrandLogo from '@/components/Brand/ProductBrandLogo'
import ProductBrandLogoWithLink from '@/components/Brand/ProductBrandLogoWithLink'

interface CustomAuthFormProps {
    otpSentTo: string;
    setOtpSentTo: React.Dispatch<React.SetStateAction<string>>;
    setDeferSignedInRedirectToAuthFlow: React.Dispatch<React.SetStateAction<boolean>>;
    initialPendingPasswordReset: PendingPasswordResetState | null;
    authSearch: string;
    authEntryPath: string;
    entryNotice?: React.ReactNode;
}

type PasswordResetState =
    | 'idle'
    | 'verifying-code'
    | 'updating-password'
    | 'success'
    | 'error';

const AUTH_LEGAL_LINKS = {
    bustly: {
        terms: 'https://www.bustly.ai/term_of_service',
        privacy: 'https://www.bustly.ai/privacy',
    },
    plugin: {
        terms: 'https://www.bizsidekick.app/term_of_service',
        privacy: 'https://www.bizsidekick.app/privacy',
    },
};

const getAuthLegalLinks = () => (
    isPluginProduct(getCurrentProductCode())
        ? AUTH_LEGAL_LINKS.plugin
        : AUTH_LEGAL_LINKS.bustly
);

type ReferralResolutionState =
    | { status: 'not_applicable' }
    | { status: 'loading' }
    | { status: 'accepted'; attribution: ReferralAttribution }
    | { status: 'invalid'; message: string };

type ReferralResolveResponse = {
    accepted?: boolean;
    attributionToken?: string | null;
    inviterDisplayName?: string | null;
    message?: string | null;
};

const CustomAuthForm: React.FC<CustomAuthFormProps> = ({
    otpSentTo,
    setOtpSentTo,
    setDeferSignedInRedirectToAuthFlow,
    initialPendingPasswordReset,
    authSearch,
    authEntryPath,
    entryNotice,
}) => {
    const { t } = useTranslation();
    const { modal } = useAdminContext();
    const { showToast } = useToast()
    const [step, setStep] = useState<'email' | 'choose_method' | 'otp' | 'password' | 'set_password' | 'forgot_password_otp'>(
        initialPendingPasswordReset ? 'forgot_password_otp' : 'email',
    )
    const [userType, setUserType] = useState<'unknown' | 'new' | 'invited' | 'existing'>('unknown')
    const [email, setEmail] = useState(initialPendingPasswordReset?.email || '')
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [resetEmail, setResetEmail] = useState(initialPendingPasswordReset?.email || '')
    const [resetLoading, setResetLoading] = useState(false)
    const [emailError, setEmailError] = useState('')
    const [passwordError, setPasswordError] = useState('')
    const [confirmPasswordError, setConfirmPasswordError] = useState('')
    const [otpCode, setOtpCode] = useState('')
    const [otpLoading, setOtpLoading] = useState(false)
    const [otpVerifyLoading, setOtpVerifyLoading] = useState(false)
    const [otpRedirecting, setOtpRedirecting] = useState(false)
    const [otpCooldown, setOtpCooldown] = useState(0)
    const [otpMessage, setOtpMessage] = useState('')
    const [passwordResetState, setPasswordResetState] = useState<PasswordResetState>('idle')
    const [passwordResetSessionVerified, setPasswordResetSessionVerified] = useState(
        initialPendingPasswordReset?.sessionVerified === true,
    )
    const { setProfile } = useAuth()
    const legalLinks = getAuthLegalLinks()

    const isPasswordResetSubmitting =
        passwordResetState === 'verifying-code'
        || passwordResetState === 'updating-password';

    const trackAuthIntent = (method: 'email' | 'email_code' | 'google') => {
        trackGoogleAnalyticsEvent(userType === 'new' ? 'sign_up' : 'login', {
            entry_point: 'admin_auth',
            method,
            transport_type: 'beacon'
        })
    }

    const trackGoogleAuthClick = () => {
        trackGoogleAnalyticsEvent('google_auth_click', {
            entry_point: 'admin_auth',
            auth_mode: userType === 'new' ? 'sign_up' : 'login',
            transport_type: 'beacon'
        })
    }

    const getAuthReturnUrl = () => {
        if (typeof window === 'undefined') return '';
        return buildAuthReturnUrl(window.location.origin, authEntryPath, authSearch);
    }

    const redirectAfterEmailOtpSignIn = (verifiedProfile: VerifyAuthResponse | null) => {
        if (typeof window === 'undefined') return;

        const redirect = getEmailOtpPostSignInRedirect(authSearch, verifiedProfile);
        if (!redirect) {
            return;
        }

        window.location.href = redirect;
    }

    const getEmailDomain = (value: string) => {
        const domain = value.split('@')[1];
        return domain ? domain.toLowerCase() : '';
    }

    const validateNewPassword = (nextPassword: string): boolean => {
        const message = getNewPasswordError(nextPassword, t);
        setPasswordError(message || '');
        return message === null;
    }

    const getPasswordResetButtonText = () => {
        if (isPasswordResetSubmitting) {
            return t('website.auth.resetUpdatingPassword');
        }

        return t('website.auth.updatePassword');
    }

    const shouldCaptureAuthError = (authError: any) => {
        const status = Number(authError?.status);
        const message = String(authError?.message || '');
        return !status
            || status >= 500
            || message.includes('Failed to fetch')
            || message.includes('Network');
    }

    // 验证邮箱格式的正则表达式
    const isValidEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return emailRegex.test(email)
    }

    useEffect(() => {
        if (otpCooldown <= 0) return;

        const timer = window.setTimeout(() => {
            setOtpCooldown((value) => Math.max(0, value - 1));
        }, 1000);

        return () => window.clearTimeout(timer);
    }, [otpCooldown]);

    // 转换 Supabase 错误为友好的错误消息
    const getFriendlyErrorMessage = (error: any): string => {
        const status = error?.status
        const message = error?.message || ''
        const passwordUpdateError = getPasswordUpdateError(error, t);
        if (passwordUpdateError) return passwordUpdateError;

        // 区分验证码错误和过期
        if (message.includes('verification code') || message.includes('otp') || message.includes('token') || message.includes('code')) {
            if (message.includes('expired') || message.includes('Expired')) {
                if (message.includes('invalid') || message.includes('Invalid')) {
                    return t('website.auth.errors.incorrectVerificationCode')
                }
                return t('website.auth.errors.expiredVerificationCode')
            }
            if (message.includes('invalid') || message.includes('Invalid') || message.includes('incorrect') || message.includes('Incorrect')) {
                return t('website.auth.errors.incorrectVerificationCode')
            }
        }

        // 根据状态码判断
        switch (status) {
            case 400:
                if (message.includes('Invalid login credentials')) {
                    return t('website.auth.errors.passwordIncorrect')
                }
                if (message.includes('Email not confirmed')) {
                    return t('website.auth.errors.verifyEmailFirst')
                }
                if (message.includes('User already registered')) {
                    return t('website.auth.errors.emailAlreadyUsed')
                }
                return t('website.auth.errors.checkAndTryAgain')

            case 409:
                return t('website.auth.errors.emailAlreadyUsed')

            case 422:
                return t('website.auth.errors.checkAndTryAgain')

            case 429:
                return t('website.auth.errors.slowDown')

            case 500:
            case 503:
                return t('website.auth.errors.serverMoment')

            default:
                // 如果没有状态码，回退到消息匹配
                if (message.includes('Invalid login credentials')) {
                    return t('website.auth.errors.passwordIncorrect')
                }
                if (message.includes('Email not confirmed')) {
                    return t('website.auth.errors.verifyEmailFirst')
                }
                if (message.includes('User already registered')) {
                    return t('website.auth.errors.emailAlreadyUsed')
                }
                if (message.includes('signups are disabled')) {
                    return t('website.auth.errors.signUpUnavailable')
                }
                if (message.includes('rate limit') || message.includes('too many requests')) {
                    return t('website.auth.errors.slowDown')
                }
                if (message.includes('Failed to fetch') || message.includes('Network')) {
                    return t('website.auth.errors.connectionTrouble')
                }
                return message
        }
    }

    const checkEmailRegistered = async (emailToCheck: string): Promise<{ exists: boolean; needsPasswordSetup: boolean; passwordSetupFlow?: string }> => {
        // Developer testing helpers
        if (emailToCheck.includes('newuser') || emailToCheck.includes('signup')) {
            return { exists: false, needsPasswordSetup: false };
        }
        if (emailToCheck.includes('invited')) {
            return { exists: true, needsPasswordSetup: true, passwordSetupFlow: 'workspace_invite' };
        }
        try {
            const data = await authService.checkEmailRegistered(
                emailToCheck,
                getProductScopedAuthRequestConfig(),
            );
            return {
                exists: data.exists === true,
                needsPasswordSetup: data.needsPasswordSetup === true,
                passwordSetupFlow: data.passwordSetupFlow,
            };
        } catch (e) {
            console.warn("Failed to check email status via backend API, falling back to existing user:", e);
        }
        return { exists: true, needsPasswordSetup: false }; // Default to existing user if check fails
    }

    const handleEmailSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')
        setEmailError('')

        if (!email) {
            setEmailError(t('website.auth.validation.enterEmail'))
            return
        } else if (!isValidEmail(email)) {
            setEmailError(t('website.auth.validation.enterValidEmail'))
            return
        }

        setLoading(true)
        try {
            clearPendingPasswordSetup();
            clearPendingPasswordReset();
            setDeferSignedInRedirectToAuthFlow(false);
            // 1. Check account status: exists? needs password setup?
            const { exists, needsPasswordSetup, passwordSetupFlow } = await checkEmailRegistered(email);

            logSentryEvent('auth.email_submit.check', {
                area: 'auth',
                email_domain: getEmailDomain(email),
                is_registered: exists,
                needs_password_setup: needsPasswordSetup,
                password_setup_flow: passwordSetupFlow || null,
            });

            if (!exists || needsPasswordSetup) {
                // New or unfinished email-code user: auto-send OTP → verify → set password → onboarding
                setUserType(passwordSetupFlow === 'workspace_invite' ? 'invited' : 'new');
                await sendEmailOtp(authService, {
                    email,
                    axiosRequestConfig: getProductScopedAuthRequestConfig(),
                })
                setOtpSentTo(email)
                setOtpCooldown(EMAIL_OTP_RESEND_COOLDOWN_SECONDS)
                setDeferSignedInRedirectToAuthFlow(true)
                setStep('otp')
            } else {
                // Existing user: choose login method (Welcome Back)
                setUserType('existing');
                setStep('choose_method')
            }
        } catch (err: any) {
            const friendlyError = getFriendlyErrorMessage(err);
            setError(friendlyError);
        } finally {
            setLoading(false);
        }
    }

    const handleEmailPasswordLogin = async (e: React.FormEvent) => {
        e.preventDefault()
        trackAuthIntent('email')
        logSentryEvent('auth.email_password.submit', {
            area: 'auth',
            email_domain: getEmailDomain(email),
        });

        // 验证密码
        if (!password) {
            setPasswordError(t('website.auth.validation.enterPassword'))
            return
        } else {
            setPasswordError('')
        }

        clearPendingPasswordReset()
        setOtpSentTo('')
        setDeferSignedInRedirectToAuthFlow(false)
        setLoading(true)
        setError('')
        try {
            const { error } = await supabase.auth.signInWithPassword({
                email,
                password
            })
            if (error) throw error
            logSentryEvent('auth.email_password.success', {
                area: 'auth',
                email_domain: getEmailDomain(email),
            });
        } catch (err: any) {
            const friendlyError = getFriendlyErrorMessage(err);
            logSentryEvent('auth.email_password.failed', {
                area: 'auth',
                email_domain: getEmailDomain(email),
                status: err?.status,
                message: friendlyError,
            }, 'warn');
            if (shouldCaptureAuthError(err)) {
                captureSentryError(err, {
                    area: 'auth',
                    status: err?.status,
                    message: friendlyError,
                });
            }
            setError(friendlyError)
        } finally {
            setLoading(false)
        }
    }

    const handleSetPassword = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')
        setPasswordError('')

        if (!password) {
            setPasswordError(t('website.auth.validation.enterPassword'))
            return
        }

        if (!validateNewPassword(password)) {
            return
        }

        setLoading(true)
        try {
            const { error } = await supabase.auth.updateUser({
                password,
                data: { password_setup_completed: true },
            });
            if (error) throw error;
            clearPendingPasswordSetup();

            showToast(
                userType === 'invited'
                    ? t('website.auth.passwordSetSuccessfully')
                    : t('website.auth.accountSetupComplete'),
                'success'
            );
            window.location.href = getPasswordSetupRedirect(authSearch, userType);
        } catch (err: any) {
            const friendlyError = getFriendlyErrorMessage(err);
            setError(friendlyError);
        } finally {
            setLoading(false);
        }
    }

    const handleGoogleAuth = async () => {
        try {
            clearPendingPasswordReset()
            setDeferSignedInRedirectToAuthFlow(false)
            trackGoogleAuthClick()
            trackAuthIntent('google')
            logSentryEvent('auth.oauth.started', {
                area: 'auth',
                auth_mode: userType === 'new' ? 'sign_up' : 'login',
                provider: 'google',
            });
            const { error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {redirectTo: getAuthReturnUrl()}
            })
            if (error) throw error
            logSentryEvent('auth.oauth.redirect_started', {
                area: 'auth',
                auth_mode: userType === 'new' ? 'sign_up' : 'login',
                provider: 'google',
            });
        } catch (err: any) {
            const friendlyError = getFriendlyErrorMessage(err)
            logSentryEvent('auth.oauth.failed', {
                area: 'auth',
                auth_mode: userType === 'new' ? 'sign_up' : 'login',
                provider: 'google',
                status: err?.status,
                message: friendlyError,
            }, 'warn');
            captureSentryError(err, {
                area: 'auth',
                auth_mode: userType === 'new' ? 'sign_up' : 'login',
                provider: 'google',
                status: err?.status,
                message: friendlyError,
            });
            setError(friendlyError)
            showToast(friendlyError, 'error')
        }
    }
    
    const sendForgotPasswordCode = async (emailToReset: string) => {
        setError('')
        setEmailError('')
        clearPendingPasswordReset()
        setDeferSignedInRedirectToAuthFlow(false)

        const normalizedResetEmail = emailToReset.trim()

        if (!normalizedResetEmail) {
            setEmailError(t('website.auth.validation.enterEmail'))
            return
        } else if (!isValidEmail(normalizedResetEmail)) {
            setEmailError(t('website.auth.validation.enterValidEmail'))
            return
        }

        setEmail(normalizedResetEmail)
        setResetEmail(normalizedResetEmail)
        setResetLoading(true)
        try {
            logSentryEvent('auth.password_reset.send_code', {
                area: 'auth',
                email_domain: getEmailDomain(normalizedResetEmail),
            });
            await sendEmailOtp(authService, {
                email: normalizedResetEmail,
                axiosRequestConfig: getProductScopedAuthRequestConfig(),
            })
            setOtpSentTo(normalizedResetEmail)
            setOtpCooldown(EMAIL_OTP_RESEND_COOLDOWN_SECONDS)
            setOtpCode('')
            setPassword('')
            setConfirmPassword('')
            setPasswordResetState('idle')
            setPasswordResetSessionVerified(false)
            markPendingPasswordReset({
                email: normalizedResetEmail,
                sessionVerified: false,
            })
            setDeferSignedInRedirectToAuthFlow(true)
            setStep('forgot_password_otp')
        } catch (err: any) {
            const friendlyError = getFriendlyErrorMessage(err);
            setError(friendlyError);
        } finally {
            setResetLoading(false);
        }
    }

    const handleResendForgotPasswordOtp = async () => {
        setError('')
        setResetLoading(true)
        try {
            await sendEmailOtp(authService, {
                email: resetEmail,
                axiosRequestConfig: getProductScopedAuthRequestConfig(),
            })
            setOtpCooldown(EMAIL_OTP_RESEND_COOLDOWN_SECONDS)
            showToast(t('website.auth.verificationCodeResent'), 'success')
        } catch (err: any) {
            setError(getFriendlyErrorMessage(err))
        } finally {
            setResetLoading(false)
        }
    }

    const handleUpdatePassword = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')
        setPasswordError('')
        setConfirmPasswordError('')

        if (!passwordResetSessionVerified && (!otpCode || otpCode.trim().length < 6)) {
            setError(t('website.auth.validation.enterSixDigitCode'))
            return
        }
        if (!password) {
            setPasswordError(t('website.auth.validation.enterNewPassword'))
            return
        }
        if (!validateNewPassword(password)) {
            return
        }
        if (password !== confirmPassword) {
            setConfirmPasswordError(t('website.auth.validation.passwordsDoNotMatch'))
            return
        }

        let resetSessionEstablished = passwordResetSessionVerified
        markPendingPasswordReset({
            email: resetEmail || otpSentTo,
            sessionVerified: passwordResetSessionVerified,
        })
        setDeferSignedInRedirectToAuthFlow(true)
        setPasswordResetState(passwordResetSessionVerified ? 'updating-password' : 'verifying-code')
        try {
            logSentryEvent('auth.password_reset.verify_and_update', {
                area: 'auth',
                email_domain: getEmailDomain(resetEmail),
            });

            if (!passwordResetSessionVerified) {
                // 1. Verify email OTP (logs the user in via supabase)
                await verifyEmailOtp(authService, supabase, {
                    email: resetEmail,
                    token: otpCode.trim(),
                    axiosRequestConfig: getProductScopedAuthRequestConfig(),
                })
                resetSessionEstablished = true
                setPasswordResetSessionVerified(true)
                markPendingPasswordReset({
                    email: resetEmail || otpSentTo,
                    sessionVerified: true,
                })
            }

            // 2. Update user password
            setPasswordResetState('updating-password')
            const { error: updateError } = await supabase.auth.updateUser({
                password,
                data: { password_setup_completed: true },
            })
            if (updateError) throw updateError

            if (readReferralCode(authSearch)) {
                setPasswordResetState('success')
                clearPendingPasswordReset()
                setDeferSignedInRedirectToAuthFlow(false)
                return // AuthPage now owns profile resolution and the destination.
            }


            // 3. Load profile
            let verifiedProfile = null
            try {
                verifiedProfile = await authService.verifyAuth(getProductScopedAuthRequestConfig())
                setProfile(verifiedProfile)
            } catch (profileErr) {
                console.warn('verifyAuth failed after password update, proceeding:', profileErr)
            }

            // 4. Redirect
            const searchParams = new URLSearchParams(authSearch);
            const redirect = searchParams.get('redirect');
            setPasswordResetState('success')
            clearPendingPasswordReset()
            setDeferSignedInRedirectToAuthFlow(false)
            window.location.href = resolveBustlyWebRedirect(redirect);
        } catch (err: any) {
            const friendlyError = getFriendlyErrorMessage(err)
            const passwordUpdateError = getPasswordUpdateError(err, t)
            setPasswordResetState('error')
            setDeferSignedInRedirectToAuthFlow(true)
            setStep('forgot_password_otp')
            logSentryEvent('auth.password_reset.failed', {
                area: 'auth',
                status: err?.status,
                message: friendlyError,
                reset_session_established: resetSessionEstablished,
            }, 'warn');
            if (passwordUpdateError) {
                setPasswordError(passwordUpdateError)
            } else {
                setError(friendlyError)
            }
        }
    }

    const handleBackFromPasswordReset = async () => {
        setError('')
        setPasswordError('')
        setConfirmPasswordError('')
        setPasswordResetState('idle')

        const emailToKeep = resetEmail || otpSentTo || email
        const resetSessionWasEstablished =
            passwordResetSessionVerified
            || readPendingPasswordReset()?.sessionVerified === true

        if (resetSessionWasEstablished) {
            setDeferSignedInRedirectToAuthFlow(true)
            try {
                await supabase.auth.signOut({ scope: 'local' })
            } catch (signOutErr) {
                logSentryEvent('auth.password_reset.session_cleanup_failed', {
                    area: 'auth',
                    has_email: Boolean(emailToKeep),
                }, 'warn')
                console.warn('Failed to sign out after abandoning password reset:', signOutErr)
                setError(t('website.auth.errors.checkAndTryAgain'))
                return
            }
        }

        setPasswordResetSessionVerified(false)
        clearPendingPasswordReset()
        setOtpSentTo('')
        setOtpCode('')
        setPassword('')
        setConfirmPassword('')
        if (!resetSessionWasEstablished) {
            setDeferSignedInRedirectToAuthFlow(false)
        }

        if (emailToKeep) {
            setEmail(emailToKeep)
            setResetEmail(emailToKeep)
            setUserType('existing')
        }

        setStep(emailToKeep ? 'password' : 'email')
    }

    const handleSendEmailOtp = async (e?: React.FormEvent | React.MouseEvent) => {
        if (e) e.preventDefault()
        trackAuthIntent('email_code')
        setError('')
        setOtpMessage('')
        clearPendingPasswordReset()
        setDeferSignedInRedirectToAuthFlow(true)

        setOtpLoading(true)
        try {
            logSentryEvent('auth.email_otp.submit', {
                area: 'auth',
                email_domain: getEmailDomain(email),
            });
            await sendEmailOtp(authService, {
                email,
                axiosRequestConfig: getProductScopedAuthRequestConfig(),
            })
            setOtpSentTo(email)
            setOtpCooldown(EMAIL_OTP_RESEND_COOLDOWN_SECONDS)
            setOtpMessage('Verification code sent. Please check your inbox.')
            logSentryEvent('auth.email_otp.sent', {
                area: 'auth',
                email_domain: getEmailDomain(email),
            });
        } catch (err: any) {
            const friendlyError = getFriendlyErrorMessage(err)
            logSentryEvent('auth.email_otp.failed', {
                area: 'auth',
                status: err?.status,
                message: friendlyError,
            }, 'warn');
            if (shouldCaptureAuthError(err)) {
                captureSentryError(err, {
                    area: 'auth',
                    flow: 'email_otp',
                    status: err?.status,
                    message: friendlyError,
                });
            }
            setError(friendlyError)
        } finally {
            setOtpLoading(false)
        }
    }

    const handleVerifyEmailOtp = async (e: React.FormEvent) => {
        e.preventDefault()
        setError('')
        setOtpMessage('')

        if (!otpCode.trim() || otpCode.trim().length < 6) {
            setError(t('website.auth.validation.enterSixDigitCode'))
            return
        }

        setOtpVerifyLoading(true)
        let redirectingAfterSuccess = false
        const requiresPasswordSetup = userType === 'new' || userType === 'invited'
        try {
            const targetEmail = otpSentTo || email
            if (requiresPasswordSetup) {
                markPendingPasswordSetup()
            } else {
                clearPendingPasswordSetup()
            }

            await verifyEmailOtp(authService, supabase, {
                email: targetEmail,
                token: otpCode.trim(),
                axiosRequestConfig: getProductScopedAuthRequestConfig(),
            })

            if (!requiresPasswordSetup && readReferralCode(authSearch)) {
                redirectingAfterSuccess = true
                setOtpRedirecting(true)
                setDeferSignedInRedirectToAuthFlow(false)
                return // AuthPage resolves the workspace, with visible failure/retry.
            }

            // OTP verified successfully — now try to load profile
            let verifiedProfile = null
            try {
                verifiedProfile = await authService.verifyAuth(getProductScopedAuthRequestConfig())
                setProfile(verifiedProfile)
            } catch (profileErr) {
                // New user or user without workspace — verifyAuth may fail, that's OK
                console.warn('verifyAuth failed after OTP verification, proceeding:', profileErr)
            }
            
            if (requiresPasswordSetup) {
                setStep('set_password');
            } else {
                redirectingAfterSuccess = true
                setOtpRedirecting(true)
                logSentryEvent('auth.email_otp.success', {
                    area: 'auth',
                    email_domain: getEmailDomain(targetEmail),
                });
                if (verifiedProfile) {
                    redirectAfterEmailOtpSignIn(verifiedProfile)
                } else {
                    // Misdetected as existing user but no workspace — redirect to onboarding
                    const searchParams = new URLSearchParams(authSearch);
                    const redirect = searchParams.get('redirect');
                    window.location.href = resolveBustlyWebRedirect(redirect);
                }
            }
        } catch (err: any) {
            if (requiresPasswordSetup) {
                clearPendingPasswordSetup()
            }
            const friendlyError = getFriendlyErrorMessage(err)
            logSentryEvent('auth.email_otp.verify_failed', {
                area: 'auth',
                status: err?.status,
                message: friendlyError,
            }, 'warn');
            if (shouldCaptureAuthError(err)) {
                captureSentryError(err, {
                    area: 'auth',
                    flow: 'email_otp_verify',
                    status: err?.status,
                    message: friendlyError,
                });
            }
            setError(friendlyError)
        } finally {
            if (!redirectingAfterSuccess) {
                setOtpVerifyLoading(false)
            }
        }
    }
    
    return (
        <div className={styles.customAuthForm}>
            {step === 'email' && (
                <>
                    <div className={styles.description}>
                        <h2 className={styles.info}>{t('login.signIn')}</h2>
                    </div>
                    {entryNotice}

                    <div className={styles.oauthButtons}>
                        <button 
                            type="button"
                            onClick={handleGoogleAuth}
                            className={styles.googleButton}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24">
                                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                            </svg>
                            {t('website.auth.continueWithGoogle')}
                        </button>
                    </div>
                    
                    <div className={styles.divider}>
                        <span>{t('website.auth.or')}</span>
                    </div>

                    <form onSubmit={handleEmailSubmit} className={styles.form} noValidate>
                        <div className={styles.inputGroup}>
                            <input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value)
                                    if (emailError) setEmailError('')
                                }}
                                placeholder={t('website.auth.emailPlaceholder')}
                                style={{border: emailError ? '1px solid red' : undefined}}
                            />
                            {emailError ? (
                                <div style={{ color: 'red', fontSize: '14px', marginTop: '4px' }}>
                                    {emailError}
                                </div>
                            ) : null}
                        </div>

                        {error ? (
                            <div style={{color: 'red', fontSize: '14px'}}>{error}</div>
                        ) : null}
                        
                        <button 
                            type="submit" 
                            className={styles.primaryButton}
                            disabled={loading}
                        >
                            {loading && (
                                <svg className={styles.spinner} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                    <circle cx="12" cy="12" r="10" stroke="rgba(255, 255, 255, 0.25)" />
                                    <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
                                </svg>
                            )}
                            {t('common.continue')}
                        </button>
                    </form>
                </>
            )}

            {step === 'choose_method' && (
                <>
                    <div className={styles.description}>
                        <h2 className={styles.info}>{t('website.auth.welcomeBack')}</h2>
                        <p className={styles.tip}>{email}</p>
                    </div>

                    <div className={styles.form}>
                        <button
                            type="button"
                            className={styles.primaryButton}
                            disabled={otpLoading}
                            onClick={async () => {
                                setError('')
                                await handleSendEmailOtp()
                                setStep('otp')
                            }}
                        >
                            {otpLoading && (
                                <svg className={styles.spinner} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                    <circle cx="12" cy="12" r="10" stroke="rgba(255, 255, 255, 0.25)" />
                                    <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
                                </svg>
                            )}
                            {t('website.auth.continueWithVerificationCode')}
                        </button>

                        <button
                            type="button"
                            className={styles.secondaryButton}
                            onClick={() => {
                                setError('')
                                setDeferSignedInRedirectToAuthFlow(false)
                                setStep('password')
                            }}
                        >
                            {t('website.auth.continueWithPassword')}
                        </button>

                        <div className={styles.toggleMode}>
                            <button
                                type="button"
                                className={styles.linkButton}
                                onClick={() => {
                                    setError('')
                                    setDeferSignedInRedirectToAuthFlow(false)
                                    setStep('email')
                                }}
                            >
                                {t('common.back')}
                            </button>
                        </div>
                    </div>
                </>
            )}

            {step === 'otp' && (
                <div className={styles.verificationContainer}>
                    <div className={styles.description}>
                        <h2 className={styles.info}>{t('website.auth.verifyEmailAddress')}</h2>
                        <p className={styles.tip}>
                            {t('website.auth.verifyEmailDescription', { email: otpSentTo || email })}
                        </p>
                    </div>

                    <form onSubmit={handleVerifyEmailOtp} className={styles.form} noValidate>
                        <div className={styles.inputGroup}>
                            <div className={styles.embeddedInputRow}>
                                <input
                                    id="otpCode"
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={6}
                                    autoComplete="one-time-code"
                                    value={otpCode}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                                        setOtpCode(val);
                                        if (val.length === 6) {
                                            setError('');
                                        }
                                    }}
                                    placeholder={t('website.auth.verificationCodePlaceholder')}
                                    autoFocus
                                />
                                <span className={styles.embeddedDivider}></span>
                                {otpCooldown > 0 ? (
                                    <span className={styles.countdownText}>{otpCooldown}</span>
                                ) : (
                                    <button
                                        type="button"
                                        className={styles.embeddedTextButton}
                                        disabled={otpLoading}
                                        onClick={handleSendEmailOtp}
                                    >
                                        {otpLoading ? t('chatTimeline.sending') : t('website.auth.resend')}
                                    </button>
                                )}
                            </div>
                        </div>


                        {error ? (
                            <div style={{color: 'red', fontSize: '14px', textAlign: 'center'}}>{error}</div>
                        ) : null}

                        <button
                            type="submit"
                            className={styles.primaryButton}
                            disabled={otpVerifyLoading || otpRedirecting || otpCode.length < 6}
                        >
                            {(otpVerifyLoading || otpRedirecting) && (
                                <svg className={styles.spinner} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                    <circle cx="12" cy="12" r="10" stroke="rgba(255, 255, 255, 0.25)" />
                                    <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
                                </svg>
                            )}
                            {otpVerifyLoading || otpRedirecting ? t('common.verifying') : t('common.verify')}
                        </button>

                        <div className={styles.toggleMode}>
                            {userType === 'existing' && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setError('')
                                        setOtpSentTo('')
                                        setDeferSignedInRedirectToAuthFlow(false)
                                        setStep('password')
                                    }}
                                    className={styles.secondaryLinkButton}
                                >
                                    {t('website.auth.signInWithPasswordInstead')}
                                </button>
                            )}
                            <button
                                type="button"
                                className={styles.linkButton}
                                onClick={() => {
                                    setOtpSentTo('')
                                    setOtpCode('')
                                    setOtpMessage('')
                                    setError('')
                                    setDeferSignedInRedirectToAuthFlow(false)
                                    setStep('email')
                                }}
                            >
                                {t('common.back')}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {step === 'password' && (
                <>
                    <div className={styles.description}>
                        <h2 className={styles.info}>{t('website.auth.enterPassword')}</h2>
                    </div>

                    <form onSubmit={handleEmailPasswordLogin} className={styles.form} noValidate>
                        <div className={styles.inputGroup}>
                            <label htmlFor="emailDisplay">{t('website.auth.email')}</label>
                            <div className={styles.emailDisplayContainer}>
                                <input
                                    id="emailDisplay"
                                    type="text"
                                    value={email}
                                    readOnly
                                    className={styles.emailReadOnlyInput}
                                />
                                <button
                                    type="button"
                                    className={styles.editEmailButton}
                                    onClick={() => {
                                        setError('')
                                        setStep('email')
                                    }}
                                >
                                    {t('website.auth.editEmail')}
                                </button>
                            </div>
                        </div>

                        <div className={styles.inputGroup}>
                            <div className={styles.passwordLabelRow}>
                                <label htmlFor="password">{t('website.auth.password')}</label>
                                <button
                                    type="button"
                                    disabled={resetLoading}
                                    aria-busy={resetLoading}
                                    onClick={() => void sendForgotPasswordCode(email)}
                                    className={styles.forgotPasswordLink}
                                >
                                    {resetLoading ? (
                                        <>
                                            <svg className={styles.forgotPasswordSpinner} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                                <circle cx="12" cy="12" r="10" stroke="currentColor" opacity="0.25" />
                                                <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
                                            </svg>
                                            {t('website.auth.sendingEmailCode')}
                                        </>
                                    ) : (
                                        t('website.auth.forgotPassword')
                                    )}
                                </button>
                            </div>
                            <div className={styles.passwordInputWrapper}>
                                <input
                                    id="password"
                                    autoComplete="current-password"
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value)
                                        if (passwordError) setPasswordError('')
                                    }}
                                    placeholder={t('website.auth.passwordPlaceholder')}
                                    style={{border: passwordError ? '1px solid red' : undefined}}
                                />
                                <button
                                    type="button"
                                    className={styles.passwordToggle}
                                    onClick={() => setShowPassword(!showPassword)}
                                    aria-label={showPassword ? t('website.auth.hidePassword') : t('website.auth.showPassword')}
                                >
                                    {!showPassword ? (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                            <line x1="1" y1="1" x2="23" y2="23" />
                                        </svg>
                                    ) : (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                            {passwordError ? (
                                <div style={{ color: 'red', fontSize: '14px', marginTop: '4px' }}>
                                    {passwordError}
                                </div>
                            ) : null}
                        </div>
                         
                        {error ? (
                            <div style={{color: 'red', fontSize: '14px'}}>{error}</div>
                        ) : null}
                        
                        <button 
                            type="submit" 
                            className={styles.primaryButton}
                            disabled={loading}
                        >
                            {loading && (
                                <svg className={styles.spinner} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                    <circle cx="12" cy="12" r="10" stroke="rgba(255, 255, 255, 0.25)" />
                                    <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
                                </svg>
                            )}
                            {t('common.continue')}
                        </button>

                        <div className={styles.toggleMode}>
                            <button
                                type="button"
                                className={styles.secondaryLinkButton}
                                onClick={async () => {
                                    setError('')
                                    await handleSendEmailOtp()
                                    setStep('otp')
                                }}
                            >
                                {t('website.auth.signInWithVerificationCodeInstead')}
                            </button>
                            <button
                                type="button"
                                className={styles.linkButton}
                                onClick={() => {
                                    setError('')
                                    setDeferSignedInRedirectToAuthFlow(false)
                                    setStep('choose_method')
                                }}
                            >
                                {t('common.back')}
                            </button>
                        </div>
                    </form>
                </>
            )}

            {step === 'set_password' && (
                <>
                    <div className={styles.description}>
                        <h2 className={styles.info}>{t('website.auth.createAccount')}</h2>
                        <p className={styles.tip}>{t('website.auth.setPasswordToContinue')}</p>
                    </div>

                    <form onSubmit={handleSetPassword} className={styles.form} noValidate>
                        <div className={styles.inputGroup}>
                            <label htmlFor="emailDisplay">{t('website.auth.email')}</label>
                            <div className={styles.emailDisplayContainer}>
                                <input
                                    id="emailDisplay"
                                    type="text"
                                    value={email}
                                    readOnly
                                    className={styles.emailReadOnlyInput}
                                />
                                <button
                                    type="button"
                                    className={styles.editEmailButton}
                                    onClick={() => {
                                        setError('')
                                        setStep('email')
                                    }}
                                >
                                    {t('website.auth.editEmail')}
                                </button>
                            </div>
                        </div>

                        <div className={styles.inputGroup}>
                            <label htmlFor="password">{t('website.auth.password')}</label>
                            <div className={styles.passwordInputWrapper}>
                                <input
                                    id="password"
                                    autoComplete="new-password"
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value)
                                        if (passwordError) setPasswordError('')
                                        if (passwordResetState === 'error') setPasswordResetState('idle')
                                    }}
                                    placeholder={t('website.auth.passwordPlaceholder')}
                                    style={{border: passwordError ? '1px solid red' : undefined}}
                                />
                                <button
                                    type="button"
                                    className={styles.passwordToggle}
                                    onClick={() => setShowPassword(!showPassword)}
                                    aria-label={showPassword ? t('website.auth.hidePassword') : t('website.auth.showPassword')}
                                >
                                    {!showPassword ? (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                            <line x1="1" y1="1" x2="23" y2="23" />
                                        </svg>
                                    ) : (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                            {passwordError ? (
                                <div style={{ color: 'red', fontSize: '14px', marginTop: '4px' }}>
                                    {passwordError}
                                </div>
                            ) : null}
                        </div>

                        {error ? (
                            <div style={{color: 'red', fontSize: '14px'}}>{error}</div>
                        ) : null}

                        <button 
                            type="submit" 
                            className={styles.primaryButton}
                            disabled={loading}
                        >
                            {loading && (
                                <svg className={styles.spinner} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                    <circle cx="12" cy="12" r="10" stroke="rgba(255, 255, 255, 0.25)" />
                                    <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
                                </svg>
                            )}
                            {t('common.continue')}
                        </button>

                    </form>
                </>
            )}

            {step === 'forgot_password_otp' && (
                <div className={styles.verificationContainer}>
                    <div className={styles.description}>
                        <h2 className={styles.info}>{t('website.auth.resetPassword')}</h2>
                        <p className={styles.tip}>
                            {t('website.auth.resetPasswordCodeDescription', { email: otpSentTo || resetEmail })}
                        </p>
                    </div>

                    <form onSubmit={handleUpdatePassword} className={styles.form} noValidate>
                        <div className={styles.inputGroup}>
                            <label htmlFor="otpCode">{t('website.auth.verificationCode')}</label>
                            <div className={styles.embeddedInputRow}>
                                <input
                                    id="otpCode"
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={6}
                                    autoComplete="one-time-code"
                                    value={otpCode}
                                    onChange={(e) => {
                                        const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                                        setOtpCode(val);
                                        if (val.length === 6) {
                                            setError('');
                                        }
                                    }}
                                    placeholder={t('website.auth.verificationCodePlaceholder')}
                                    autoFocus
                                />
                                <span className={styles.embeddedDivider}></span>
                                {otpCooldown > 0 ? (
                                    <span className={styles.countdownText}>{otpCooldown}s</span>
                                ) : (
                                    <button
                                        type="button"
                                        className={styles.embeddedTextButton}
                                        disabled={resetLoading}
                                        onClick={handleResendForgotPasswordOtp}
                                    >
                                        {resetLoading ? t('chatTimeline.sending') : t('website.auth.resend')}
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className={styles.inputGroup}>
                            <label htmlFor="password">{t('website.auth.newPassword')}</label>
                            <div className={styles.passwordInputWrapper}>
                                <input
                                    id="password"
                                    autoComplete="new-password"
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value)
                                        if (passwordError) setPasswordError('')
                                        if (passwordResetState === 'error') setPasswordResetState('idle')
                                    }}
                                    placeholder={t('website.auth.newPasswordPlaceholder')}
                                    style={{border: passwordError ? '1px solid red' : undefined}}
                                />
                                <button
                                    type="button"
                                    className={styles.passwordToggle}
                                    onClick={() => setShowPassword(!showPassword)}
                                    aria-label={showPassword ? t('website.auth.hidePassword') : t('website.auth.showPassword')}
                                >
                                    {!showPassword ? (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                            <line x1="1" y1="1" x2="23" y2="23" />
                                        </svg>
                                    ) : (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                            {passwordError ? (
                                <div style={{ color: 'red', fontSize: '14px', marginTop: '4px' }}>
                                    {passwordError}
                                </div>
                            ) : null}
                        </div>

                        <div className={styles.inputGroup}>
                            <label htmlFor="confirmPassword">{t('website.auth.confirmPassword')}</label>
                            <div className={styles.passwordInputWrapper}>
                                <input
                                    id="confirmPassword"
                                    autoComplete="new-password"
                                    type={showPassword ? "text" : "password"}
                                    value={confirmPassword}
                                    onChange={(e) => {
                                        setConfirmPassword(e.target.value)
                                        if (confirmPasswordError) setConfirmPasswordError('')
                                        if (passwordResetState === 'error') setPasswordResetState('idle')
                                    }}
                                    placeholder={t('website.auth.confirmNewPasswordPlaceholder')}
                                    style={{border: confirmPasswordError ? '1px solid red' : undefined}}
                                />
                                <button
                                    type="button"
                                    className={styles.passwordToggle}
                                    onClick={() => setShowPassword(!showPassword)}
                                    aria-label={showPassword ? t('website.auth.hidePassword') : t('website.auth.showPassword')}
                                >
                                    {!showPassword ? (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                            <line x1="1" y1="1" x2="23" y2="23" />
                                        </svg>
                                    ) : (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                            <circle cx="12" cy="12" r="3" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                            {confirmPasswordError ? (
                                <div style={{ color: 'red', fontSize: '14px', marginTop: '4px' }}>
                                    {confirmPasswordError}
                                </div>
                            ) : null}
                        </div>

                        {error ? (
                            <div style={{color: 'red', fontSize: '14px', textAlign: 'center'}}>{error}</div>
                        ) : null}

                        <button
                            type="submit"
                            className={styles.primaryButton}
                            disabled={isPasswordResetSubmitting || (!passwordResetSessionVerified && otpCode.length < 6) || !password || !confirmPassword}
                        >
                            {isPasswordResetSubmitting && (
                                <svg className={styles.spinner} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                    <circle cx="12" cy="12" r="10" stroke="rgba(255, 255, 255, 0.25)" />
                                    <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
                                </svg>
                            )}
                            {getPasswordResetButtonText()}
                        </button>

                        <div className={styles.toggleMode}>
                            <button
                                type="button"
                                className={styles.linkButton}
                                disabled={isPasswordResetSubmitting}
                                onClick={handleBackFromPasswordReset}
                            >
                                {t('common.back')}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <p className={styles.legalText}>
                {t('website.auth.legalPrefix')}{' '}
                <a href={legalLinks.terms} target="_blank" rel="noopener noreferrer">{t('website.auth.termsOfService')}</a>
                {' '}{t('website.auth.legalAnd')}{' '}
                <a href={legalLinks.privacy} target="_blank" rel="noopener noreferrer">{t('website.auth.privacyPolicy')}</a>.
            </p>
        </div>
    )
}

export default function AuthPage() {
    const { t } = useTranslation();
    const [initialPendingPasswordReset] = useState(() => readPendingPasswordReset())
    const [otpSentTo, setOtpSentTo] = useState(initialPendingPasswordReset?.email || '')
    const [deferSignedInRedirectToAuthFlow, setDeferSignedInRedirectToAuthFlow] = useState(() => hasPendingPasswordReset())
    const [referralSignInState, setReferralSignInState] = useState<ReferralSignInState>('idle');
    const [referralSignInAttempt, setReferralSignInAttempt] = useState(0);
    const rawReferralCode = readReferralCode(window.location.search);
    const isCoreReferralEntry = !isPluginProduct(getCurrentProductCode()) && Boolean(rawReferralCode);
    const isLocalReferralPreview = import.meta.env.DEV
        && isReferralAuthPreview(window.location.search);
    const [authSearch] = useState(() => (
        isCoreReferralEntry
            ? buildReferralAuthSearch(window.location.search, window.location.origin)
            : window.location.search
    ));
    const authEntryPath = window.location.pathname === '/join' ? '/join' : '/admin/auth';
    const searchParams = new URLSearchParams(authSearch);
    const connect = searchParams.get('connect');
    const platformId = searchParams.get('platformId');
    const platformUserName = searchParams.get('platformUserName');
    const redirect = searchParams.get('redirect');
    const referralCode = readReferralCode(authSearch);
    const [referralResolution, setReferralResolution] = useState<ReferralResolutionState>(() => {
        if (!isCoreReferralEntry) {
            return { status: 'not_applicable' };
        }
        if (isLocalReferralPreview && referralCode) {
            return {
                status: 'accepted',
                attribution: {
                    code: referralCode,
                    attributionToken: 'local-preview-only',
                    inviterDisplayName: null,
                    acceptedAt: new Date().toISOString(),
                },
            };
        }
        return { status: 'loading' };
    });
    const { user ,profile } = useAuth();
    const { getOAuthContextFromSearch, clearOAuthContext } = useOauth();
    const oauthContextFromSearch = getOAuthContextFromSearch(authSearch);

    useEffect(() => {
        let active = true;

        if (!isCoreReferralEntry || !referralCode) {
            setReferralResolution({ status: 'not_applicable' });
            return () => { active = false; };
        }

        if (isLocalReferralPreview) {
            return () => { active = false; };
        }

        setReferralResolution({ status: 'loading' });
        fetch(`/api/referrals/${encodeURIComponent(referralCode)}/resolve`, { cache: 'no-store' })
            .then(async (response) => {
                const payload = await response.json().catch(() => ({}));
                const view = (payload?.data || payload) as ReferralResolveResponse;
                const attributionToken = (view.attributionToken || '').trim();
                if (!response.ok || view.accepted !== true || !attributionToken) {
                    throw new Error(view.message || 'This referral link is unavailable.');
                }
                if (!active) return;

                const attribution = saveReferralAttribution({
                    code: referralCode,
                    entryId: readReferralEntryId(authSearch),
                    attributionToken,
                    inviterDisplayName: view.inviterDisplayName,
                }, window.localStorage);
                if (!attribution) {
                    throw new Error('This referral link is unavailable.');
                }
                setReferralResolution({ status: 'accepted', attribution });
            })
            .catch((error: unknown) => {
                if (!active) return;
                clearStoredReferralAttribution(window.localStorage, readReferralEntryId(authSearch));
                setReferralResolution({
                    status: 'invalid',
                    message: error instanceof Error ? error.message : 'This referral link is unavailable.',
                });
            });

        return () => { active = false; };
    }, [isCoreReferralEntry, isLocalReferralPreview, referralCode, authSearch]);

    function ConnectShopifyStatus({
        platformId, platformUserName, user, redirect,profile 
    }: { platformId: string, platformUserName: string, user: User, redirect: string,profile: VerifyAuthResponse }) {
        const [loading, setLoading] = useState(false);
        const { showToast } = useToast();
        useEffect(() => {
            location.href = resolveBustlyWebRedirect(redirect);
        }, [profile, platformUserName, redirect])

        const handleConfirm = async () => {
            try {
                setLoading(true);
                logSentryEvent('auth.shopify_bind.started', {
                    area: 'auth',
                    platform: 'shopify',
                });
                const response = await authService.bindShop({
                    accessToken: platformId,
                    domain: platformUserName
                });

                if (response === 'success') {
                    logSentryEvent('auth.shopify_bind.success', {
                        area: 'auth',
                        platform: 'shopify',
                    });
                    // Redirect to homepage after successful binding
                    location.href = resolveBustlyWebRedirect(redirect);
                } else {
                    logSentryEvent('auth.shopify_bind.failed', {
                        area: 'auth',
                        platform: 'shopify',
                        response,
                    }, 'warn');
                    showToast(t('website.auth.connectError'), 'error');
                }
            } catch (error) {
                logSentryEvent('auth.shopify_bind.failed', {
                    area: 'auth',
                    platform: 'shopify',
                }, 'error');
                captureSentryError(error, {
                    area: 'auth',
                    flow: 'shopify_bind',
                    platform: 'shopify',
                });
                showToast(t('website.auth.connectError'), 'error');
            } finally {
                setLoading(false);
            }
        };

        return (
            <div className={styles.connectContainer}>
                <div className={styles.side}>
                    <ProductBrandLogo style={{ height: 40 }} />
                    <span className={styles.label}>{user?.user_metadata?.full_name}</span>
                </div>
                <div className={styles.center}>
                    <span className={styles.line}></span>
                </div>
                <div className={styles.side}>
                    <img src={ShopifyLogo} alt="Shopify" className={styles.shopifyLogo} />
                    <span className={styles.label}>{platformUserName}</span>
                </div>
                <button
                    className={styles.confirmButton}
                    style={{ margin: "auto" }}
                    onClick={handleConfirm}
                    disabled={loading}
                >
                    {loading ? t('website.auth.connecting') : t('website.auth.connectStore')}
                </button>
            </div>
        )
    }

    useEffect(() => {
        if (oauthContextFromSearch) {
            return;
        }

        clearOAuthContext();
    }, [clearOAuthContext, oauthContextFromSearch])

    useEffect(() => {
        if (isLocalReferralPreview) {
            return;
        }

        if (!connect && user) {
            if (referralResolution.status === 'loading') {
                return;
            }
            if (oauthContextFromSearch) {
                location.href = `/admin/oauth/success${authSearch}`;
                return;
            }

            if (deferSignedInRedirectToAuthFlow) {
                return;
            }

            if (isCoreReferralEntry) {
                return startReferralSignInRouting({
                    search: authSearch,
                    loadProfile: () => authService.verifyAuth(getProductScopedAuthRequestConfig()),
                    onStateChange: (state) => {
                        setReferralSignInState(state);
                        logSentryEvent('auth.referral.destination', {
                            area: 'auth',
                            route_kind: 'referral-auth',
                            state,
                            reason: 'signed-in-workspace-resolution',
                            has_referral: Boolean(referralCode),
                        }, state === 'error' ? 'warn' : 'info');
                    },
                    navigate: (destination) => { location.href = destination; },
                    referralResolutionStatus: referralResolution.status,
                });
            }

            location.href = resolveBustlyWebRedirect(redirect);
        }
        setReferralSignInState('idle');
    }, [connect, deferSignedInRedirectToAuthFlow, isLocalReferralPreview, oauthContextFromSearch, redirect, referralResolution.status, user, isCoreReferralEntry, authSearch, referralCode, referralSignInAttempt])

    if (connect && platformId && platformUserName && user && profile) {
        return <ConnectShopifyStatus profile={profile} platformId={platformId} platformUserName={platformUserName} user={user} redirect={redirect || '/admin'} />
    }

    const authForm = (
        <div className={styles.form}>
            <CustomAuthForm
                otpSentTo={otpSentTo}
                setOtpSentTo={setOtpSentTo}
                setDeferSignedInRedirectToAuthFlow={setDeferSignedInRedirectToAuthFlow}
                initialPendingPasswordReset={initialPendingPasswordReset}
                authSearch={authSearch}
                authEntryPath={authEntryPath}
                entryNotice={isCoreReferralEntry ? (
                    <section className={styles.referralInviteNotice} aria-labelledby="referral-invite-title">
                        <div className={styles.referralGiftBadge}>
                            <Gift className={styles.referralGiftIcon} size={18} weight="duotone" aria-hidden="true" />
                        </div>
                        <div className={styles.referralInviteContent}>
                            <p id="referral-invite-title" className={styles.referralBenefitTitle}>
                                {t('website.auth.referralBenefitDescription')}
                            </p>
                            <p className={styles.referralBenefitDetails}>
                                {t('website.auth.referralBenefitDetails')}
                            </p>
                        </div>
                    </section>
                ) : undefined}
            />
        </div>
    );

    if (isCoreReferralEntry) {
        return (
            <div className={styles.referralContainer}>
                <header className={styles.referralHeader}>
                    <ProductBrandLogoWithLink size="26px" />
                </header>
                <main className={styles.referralMain}>
                    {referralResolution.status === 'loading' ? (
                        <div className={styles.referralStatus} aria-live="polite">
                            <div className={styles.referralStatusSpinner} />
                            <p>{t('website.auth.referralLoading')}</p>
                        </div>
                    ) : referralResolution.status === 'invalid' ? (
                        <div className={styles.referralStatus} role="alert">
                            <h2>{t('website.auth.referralUnavailableTitle')}</h2>
                            <p>{referralResolution.message}</p>
                            <a className={styles.referralContinueLink} href="/admin/auth">
                                {t('website.auth.referralContinueWithout')}
                            </a>
                        </div>
                    ) : referralSignInState !== 'idle' ? (
                        <div className={styles.referralStatus} aria-live="polite" aria-busy={referralSignInState !== 'error'}>
                            {referralSignInState === 'error' ? (
                                <>
                                    <p role="alert">{t('website.auth.errors.connectionTrouble')}</p>
                                    <button type="button" className={styles.referralContinueLink} onClick={() => setReferralSignInAttempt((attempt) => attempt + 1)}>
                                        {t('common.retry')}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <div className={styles.referralStatusSpinner} />
                                    <p>{t('common.loading')}</p>
                                </>
                            )}
                        </div>
                    ) : (
                        <div className={styles.referralAuthContainer}>
                            <div className={styles.referralAuthForm}>
                                <div className={styles.content}>
                                    <div className={styles.auth}>{authForm}</div>
                                </div>
                            </div>
                        </div>
                    )}
                </main>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            {/* content */}
            <div className={styles.content}>
                <div className={styles.authLogo}>
                    <ProductBrandLogoWithLink size="26px" />
                </div>
                <div className={styles.auth}>
                    {authForm}
                </div>
            </div>
        </div>
    )
}
